import React, { useRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { compose } from '@/utils/story-composer';
import BlogHeader from './blog-header';
import { renderAsset } from '@/components/elements/image';
import Wave from '@/components/elements/wave';
import Text from '@/components/elements/text';
import Carousel from '@/components/elements/carousel';
import BlogPostTile from '@/components/layout/blog/post-tile';
import Button from '../elements/button';
import { StylingEnums } from '@/enums/styling';
import { getTranslatableSlug } from '@/utils/translatable-slug';
import { formatDate } from '@/utils/date';
import Calender from '../icons/calender';
import Clock from '../icons/clock';

function BlogPost({ elementProps, textualBody, blogTitle, metadataImage, pageContext, author }) {
	const { FillColorEnum, BackgroundColorEnum, ButtonEnum } = StylingEnums;
	const { t } = useTranslation();
	const articleRef = useRef();
	const [readingTime, setReadingTime] = useState(0);

	useEffect(() => {
		const text = articleRef.current.innerText;
		const wordsPerMinute = 200;
		const words = text.trim().split(/\s+/).length;
		setReadingTime(Math.ceil(words / wordsPerMinute));
	}, []);

	const translatedSlug = getTranslatableSlug(pageContext.story, pageContext.story.lang);
	const blogHeaderTitle = blogTitle || translatedSlug?.name || pageContext.story.name;

	const blogTiles = pageContext.relatedBlogs?.map(post => (
		<BlogPostTile key={post._uid} post={post} pageContext={pageContext} isCarouselItem={true} />
	));

	return (
		<>
			<article ref={articleRef} className="flex flex-col justify-center w-full">
				<BlogHeader
					backgroundColor={BackgroundColorEnum.SKY_LIGHTEST}
					metadataImage={metadataImage}
					pageContext={pageContext}
				/>
				<Wave backgroundColor={BackgroundColorEnum.SKY_LIGHTEST} />
				<div className="mt-20 sm:mt-40 md:mt-32 lg:mt-44 2xl:mt-44">
					<div className="px-4 sm:px-8 md:px-10 lg:px-20 prose md:prose-md 2xl:prose-2xl 3xl:prose-3xl text-center ">
						<h1 className="text-midnight">{blogHeaderTitle}</h1>
						<div className="text-base flex text-shuttle-grey justify-center">
							<span className="mr-4 flex items-center">
								<Calender color="text-regent-grey mr-2" />
								{formatDate(pageContext.story.first_published_at, pageContext.story.lang)}
							</span>
							<span className="flex items-center">
								<Clock color="text-regent-grey mr-2" />
								{readingTime} {t('gynzy-com.minutes')}
							</span>
						</div>
					</div>
				</div>
				<div className="flex flex-col self-center max-w-210 2xl:max-w-230 px-4 sm:px-8 pt-10 pb-20" {...elementProps}>
					<Text text={textualBody} pageContext={{ ...pageContext, proseContainer: 'blog-post__container' }} />
					{author && typeof author === 'object' && (
						<div className="pt-20 flex flex-col">
							<div className="text-2xl font-semibold text-shuttle-grey">
								<span>
									{t('gynzy-com.about-author')} {author.name}
								</span>
							</div>
							<div className="flex flex-row pt-4">
								<div className="rounded-full overflow-hidden w-20 h-20 mr-4 shrink-0">
									{renderAsset(author.content.image, pageContext.images)}
								</div>
								<div className="text-xl">{author.content.description}</div>
							</div>
						</div>
					)}
				</div>
			</article>
			{blogTiles && blogTiles.length > 0 ? (
				<div className="bg-mariner-extra-light text-center">
					<Wave fillColor={FillColorEnum.MARINER_EXTRA_LIGHT} />
					<Carousel
						heading={t('gynzy-com.more-posts')}
						content={blogTiles}
						buttons={[
							<Button
								buttonType={ButtonEnum.PRIMARY}
								text={t('gynzy-com.more-gynzy-news')}
								link={{
									linktype: 'url',
									url: `/${pageContext.story.lang}/blog`,
								}}
							/>,
						]}
					/>
					<Wave backgroundColor={BackgroundColorEnum.MARINER_EXTRA_LIGHT} fillColor={FillColorEnum.MARINER} />
				</div>
			) : null}
		</>
	);
}

export default BlogPost;

compose('blog-post', BlogPost, { children: ['body'] });
